/**
 * t428u: Daniel: "prompt users for the necessary notifications, mic, camera etc permissions when they first sign in,
 * and prompt them regularly if they haven't". The phone web (and desktop) side; the iPhone app matches it
 * (apps/mobile/src/permissions/PhoneSetup.tsx).
 *  - PhoneSetupGate: after the first sign-in in this browser (after two-factor), one calm "Set up your phone" screen
 *    when something's still to allow. Not now / Continue closes it for good on this device.
 *  - PermissionReminder: a compact, muted line on Home while something's off. Dismissed, it comes back after 3 days at
 *    the earliest and stops after 5 dismissals.
 *  - PermissionsRow (Me › Phone permissions) and PermissionsPage (/permissions; Settings › My account on a computer).
 * Rows: icon, name, one plain line on why, and Allow / muted "On" / a line on where to allow it when blocked.
 */
import { Link, useNavigate } from 'react-router-dom';
import { Bell, Camera, ChevronLeft, ChevronRight, MapPin, Mic, X } from 'lucide-react';
import { reminderText, shouldShowReminder, shouldShowSetup, type DevicePermission, type DevicePermissionRow, type DevicePermissionStatus } from '@tradeoutlet/shared';
import { useEffect, useState } from 'react';
import { dismissPermissionReminder, markSetupDone, notificationsNeedInstall, useDevicePermissions, usePermissionPrefs } from '@/lib/devicePermissions';
import { useMobileLayout } from './layout';

const ICON = { notifications: Bell, microphone: Mic, camera: Camera, location: MapPin } as const;
const pill = 'flex min-h-[44px] shrink-0 items-center rounded-full bg-white px-4 text-sm font-semibold text-slate-900 ring-1 ring-inset ring-slate-300 active:bg-slate-100 disabled:opacity-60 dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-600 dark:active:bg-slate-800';

/** The title in Me and Settings: the phone, or this browser on a computer. */
export const usePermissionsTitle = () => (useMobileLayout() ? 'Phone permissions' : 'Browser permissions');

export function PermissionsListView({ rows, status, busy, onAllow, install }: {
  rows: DevicePermissionRow[]; status: Partial<Record<DevicePermission, DevicePermissionStatus>>; busy: DevicePermission | null; onAllow: (k: DevicePermission) => void;
  /** iPhone Safari: notifications need the Home Screen app first. */
  install?: boolean;
}) {
  return (
    <ul className="border-y border-slate-100 dark:border-slate-800" data-testid="permissions-list">
      {rows.map(r => {
        const s = status[r.kind];
        const Icon = ICON[r.kind];
        const note = s === 'denied' ? 'Blocked in this browser. Allow it in the site settings (the icon next to the web address).'
          : r.kind === 'notifications' && install ? 'On iPhone, tap Share, then Add to Home Screen, and open it from there.' : null;
        return (
          <li key={r.kind} className="flex min-h-[64px] items-center gap-3 border-t border-slate-100 px-4 py-2.5 first:border-t-0 dark:border-slate-800" data-testid={`perm-${r.kind}`} data-state={s ?? 'checking'}>
            <Icon className="h-5 w-5 shrink-0 text-slate-500" aria-hidden />
            <span className="min-w-0 flex-1">
              <span className="block text-base">{r.title}</span>
              <span className="block text-sm text-slate-500">{r.why}</span>
              {note && <span className="mt-0.5 block text-sm text-slate-500" data-testid={`perm-${r.kind}-note`}>{note}</span>}
            </span>
            {s === 'granted' ? <span className="text-sm text-slate-500" data-testid={`perm-${r.kind}-on`}>On</span>
              : s === 'ask' ? <button type="button" className={pill} disabled={busy !== null} onClick={() => onAllow(r.kind)} data-testid={`perm-${r.kind}-allow`}>Allow</button>
              : s === 'denied' ? <span className="text-sm text-slate-500">Blocked</span>
              : s === 'unknown' ? <span className="shrink-0 text-right text-sm text-slate-500">Asked when needed</span>
              : s === 'unsupported' && !(r.kind === 'notifications' && install) ? <span className="shrink-0 text-right text-sm text-slate-500">Not in this browser</span>
              : null}
          </li>
        );
      })}
    </ul>
  );
}

export function PermissionsPanel() {
  const p = useDevicePermissions();
  return <PermissionsListView rows={p.rows} status={p.status} busy={p.busy} onAllow={k => void p.request(k)} install={notificationsNeedInstall()} />;
}

/** /permissions: Me › Phone permissions and the Home reminder land here. */
export default function PermissionsPage() {
  const nav = useNavigate();
  const title = usePermissionsTitle();
  return (
    <div className="h-full overflow-y-auto pb-6" data-testid="permissions-page">
      <div className="flex h-12 items-center gap-1 border-b border-slate-200 bg-white pr-2 dark:border-slate-800 dark:bg-slate-900">
        <button type="button" onClick={() => (window.history.length > 1 ? nav(-1) : nav('/me'))} aria-label="Back" className="flex h-12 w-11 items-center justify-center text-slate-700 dark:text-slate-200"><ChevronLeft className="h-6 w-6" /></button>
        <h1 className="min-w-0 flex-1 truncate text-base font-semibold">{title}</h1>
      </div>
      <div className="mx-auto max-w-xl pt-3"><PermissionsPanel /></div>
    </div>
  );
}

export function PhoneSetupScreen({ onDone }: { onDone: () => void }) {
  const p = useDevicePermissions();
  const phone = useMobileLayout();
  const left = p.missing.length > 0;
  return (
    <div className="fixed inset-0 z-[55] flex flex-col bg-white dark:bg-slate-950" role="dialog" aria-modal="true" aria-labelledby="phone-setup-title" data-testid="phone-setup">
      <div className="mx-auto flex min-h-0 w-full max-w-xl flex-1 flex-col" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="min-h-0 flex-1 overflow-y-auto pb-4 pt-6">
          <h1 id="phone-setup-title" className="px-4 pb-4 text-2xl font-bold">{phone ? 'Set up your phone' : 'Set up this browser'}</h1>
          <PermissionsListView rows={p.rows} status={p.status} busy={p.busy} onAllow={k => void p.request(k)} install={notificationsNeedInstall()} />
        </div>
        <div className="px-4 pt-2" style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 16px)' }}>
          <button type="button" onClick={onDone} data-testid="phone-setup-done"
            className={left ? 'h-12 w-full rounded-lg bg-white text-base font-semibold text-slate-800 ring-1 ring-inset ring-slate-300 active:bg-slate-100 dark:bg-transparent dark:text-slate-100 dark:ring-slate-600'
              : 'h-12 w-full rounded-lg bg-accent text-base font-semibold text-on-accent-strong ring-1 ring-inset ring-black/10 active:opacity-80'}>
            {left ? 'Not now' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Over the signed-in app, once per browser. Never blocks: Not now is always there. */
export function PhoneSetupGate() {
  const prefs = usePermissionPrefs();
  const p = useDevicePermissions();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (prefs.setupDone || !p.ready || open) return;
    if (shouldShowSetup(prefs.setupDone, p.missing)) setOpen(true);
    else markSetupDone();
  }, [prefs.setupDone, p.ready, p.missing.length, open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open) return null;
  return <PhoneSetupScreen onDone={() => { setOpen(false); markSetupDone(); }} />;
}

export function PermissionReminderView({ message, desktop, onDismiss }: { message: string; desktop?: boolean; onDismiss: () => void }) {
  return (
    <div className={desktop ? 'mb-4 flex items-center rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900' : 'flex shrink-0 items-center border-b border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900'} data-testid="perm-reminder">
      <Link to="/permissions" className="flex min-h-[44px] min-w-0 flex-1 items-center gap-2.5 py-2 pl-4 text-[13px] leading-snug text-slate-700 active:bg-slate-100 dark:text-slate-300 dark:active:bg-slate-800" data-testid="perm-reminder-open">
        <Bell className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
        <span className="min-w-0 flex-1">{message}</span>
      </Link>
      <button type="button" onClick={onDismiss} aria-label="Dismiss" className="flex h-11 w-11 shrink-0 items-center justify-center text-slate-500 active:bg-slate-100 dark:active:bg-slate-800" data-testid="perm-reminder-dismiss">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/** Home: while something's off, at most every 3 days after a dismissal, never after 5. */
export function PermissionReminder({ desktop }: { desktop?: boolean }) {
  const prefs = usePermissionPrefs();
  const p = useDevicePermissions();
  if (!p.ready || !shouldShowReminder(prefs.reminder, p.missing, Date.now(), prefs.setupDone)) return null;
  return <PermissionReminderView message={reminderText(p.missing)} desktop={desktop} onDismiss={() => dismissPermissionReminder()} />;
}

/** Me › Phone permissions: always there, with how many are still to allow as muted text. */
export function PermissionsRow() {
  const p = useDevicePermissions();
  const title = usePermissionsTitle();
  const n = p.missing.length;
  return (
    <Link to="/permissions" className="flex min-h-[56px] w-full items-center gap-3 px-4 text-left text-base active:bg-slate-100 dark:active:bg-slate-800" data-testid="me-permissions">
      <span className="flex-1">{title}</span>
      {n > 0 && <span className="text-sm text-slate-500" data-testid="me-permissions-left">{n} to allow</span>}
      <ChevronRight className="h-5 w-5 text-slate-400" aria-hidden />
    </Link>
  );
}
